import { defineStore } from "pinia"
import { useCasesStore } from "@/stores/casesStore"
import { useGlobalStore } from "@/stores/globalStore"
import {
  resetCaseNavState,
} from "@/composables/useCaseNavigation"
import {
  resetLatestUpdatesState,
} from "@/composables/useLatestUpdates"
import {
  resetFollowUpNotificationsState,
} from "@/composables/useFollowUpNotifications"
import { resetMessagesInbox } from "@/composables/useMessagesInbox"
import { resetNotesInbox } from "@/composables/useNotesInbox"
import { resetUnifiedInbox } from "@/composables/useUnifiedInbox"
import { resetInboxFlags } from "@/composables/useInboxFlags"
import { resetInboxImpersonationState } from "@/composables/useInboxImpersonation"
import {
  LOGIN_MUTATION,
  LOGOUT_MUTATION,
  AUTHENTICATED_USER_QUERY,
} from "~/graphql/auth"

// The catalog itself lives in utils/roles.js (2026-09-11 — importable
// without dragging the store's whole graph); re-exported here so every
// existing `import { ROLES } from '@/stores/authStore'` keeps working.
export { ROLES } from '~/utils/roles'
import { ROLES } from '~/utils/roles'

/**
 * Permission constants
 */
export const PERMISSIONS = {
  FOLLOW_UPS_CHANGE_FOLLOWUP_INTERNAL: "follow_ups.change_followup_internal",
  CASES_CHANGE_CASE_STAGE: "cases.change_case_stage",
  CASES_CAN_PLANNING_CASES: "cases.can_planning_cases",
  CASES_CAN_ASSIGN_TAGS_TO_CASES: "cases.can_assign_tags_to_cases",
  CASES_CAN_FILTER: "cases.can_filter",
  FOLLOW_UPS_CAN_ASSIGN_CREATORS_TO_FOLLOW_UPS:
    "follow_ups.can_assign_creators_to_follow_ups",
  CASES_TRANSFER_CASE: "cases.transfer_case",
  CASES_CHANGE_LEAD_SOURCE: "cases.change_lead_source",
  CASES_IMPORT_PLANNING: "cases.import_planning",
  CASES_VIEW_CASE_DETAILS: "cases.view_case_details",
  CASES_EDIT_CASE: "cases.edit_case",
  STATS_VIEW: "stats.view",
  USERS_MANAGE: "users.manage",
  FOLLOW_UPS_REPORT_VIEW: "follow_ups.report.view",
  FOLLOW_UPS_MARK_CASE_READ: "follow_ups.mark_case_read",
}

// Session token storage key. Mirrored into either localStorage (persistent
// — used when the user ticks "Remember me") or sessionStorage (lifetime
// equals the browser session — used when "Remember me" is off). The real
// auth credential is the Keystone session cookie set by the server; this
// mirror only tells the app whether it's worth trying to restore the
// session on page load.
const SESSION_TOKEN_KEY = "sessionToken"
const REMEMBER_FLAG_KEY = "sessionTokenRemember"

/**
 * Authentication store to manage user authentication state
 *
 * This store handles:
 * - User authentication state (login/logout)
 * - Session token persistence
 * - Role-based access control
 * - Permission checking
 */
export const useAuthStore = defineStore("authStore", {
  state: () => ({
    _authenticatedUser: null,
    _sessionToken: null,
    _isInitialized: false,
    _remember: false,

    /**
     * True while `logout()` is running. The Apollo error link reads
     * this so a `UNAUTHENTICATED` from the `endSession` mutation (or
     * from any inflight request that resolves mid-logout) doesn't
     * fire the "session expired — please sign in again" flow on top
     * of the user's own logout.
     */
    _isLoggingOut: false,
  }),

  getters: {
    /**
     * Check if the store has been initialized
     */
    isInitialized: state => state._isInitialized,

    /**
     * Check if the user is authenticated
     */
    isAuthenticated: state => !!state._authenticatedUser,

    /**
     * Retrieve the authenticated user
     */
    authenticatedUser: state => state._authenticatedUser,

    /**
     * Get the session token
     */
    sessionToken: state => state._sessionToken,

    /**
     * True while a logout is in progress — read by the Apollo error
     * link to suppress its session-expired side effects.
     */
    isLoggingOut: state => state._isLoggingOut,

    /**
     * Check if the authenticated user is a system administrator
     */
    isSysAdmin() {
      if (!this.isAuthenticated) {
        return false
      }

      return this.authenticatedUser?.role === ROLES.SYSTEM_ADMIN
    },

    /**
     * Check if the authenticated user is a lead agency user
     */
    isLeadAgencyUser() {
      if (!this.isAuthenticated) {
        return false
      }

      const role = this.authenticatedUser?.role

      return (
        role === ROLES.LEAD_AGENCY_OWNER ||
        role === ROLES.LEAD_AGENCY_MANAGER ||
        role === ROLES.LEAD_AGENCY_SUPPORT
      )
    },

    /**
     * Check if the authenticated user is a lead agency OWNER specifically (not a
     * manager / support). Owners get company-wide reach that the other agency
     * roles don't — e.g. the "Case List (All)" report across every agency.
     */
    isLeadAgencyOwner() {
      if (!this.isAuthenticated) {
        return false
      }

      return this.authenticatedUser?.role === ROLES.LEAD_AGENCY_OWNER
    },

    /**
     * Check if the authenticated user is a company user
     */
    isCompanyUser() {
      if (!this.isAuthenticated) {
        return false
      }

      const role = this.authenticatedUser?.role

      return (
        role === ROLES.COMPANY_OWNER ||
        role === ROLES.COMPANY_MANAGER ||
        role === ROLES.COMPANY_SUPPORT
      )
    },
  },

  actions: {
    /**
     * Initialize the auth store - restore session token from storage.
     * Should be called once on app startup. Checks localStorage first
     * (persistent / Remember me), then sessionStorage (this-browser-tab
     * only). Whichever holds the token also tells us which mode the
     * user logged in under, so future writes stay in the same bucket.
     */
    init() {
      if (this._isInitialized) {
        return
      }

      if (import.meta.client) {
        const localToken = localStorage.getItem(SESSION_TOKEN_KEY)
        const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY)

        if (localToken) {
          this._sessionToken = localToken
          this._remember = true
        } else if (sessionToken) {
          this._sessionToken = sessionToken
          this._remember = false
        }
      }

      this._isInitialized = true
    },

    /**
     * Set authenticated user and token. `remember=true` persists the
     * token in localStorage so the session survives browser restarts;
     * `false` puts it in sessionStorage, which is wiped when the
     * browser session ends.
     */
    setAuth(user, token, remember = false) {
      this._authenticatedUser = user
      this._sessionToken = token
      this._remember = !!remember

      if (import.meta.client && token) {
        // Always clear the other bucket so we never have two stale tokens.
        if (remember) {
          localStorage.setItem(SESSION_TOKEN_KEY, token)
          localStorage.setItem(REMEMBER_FLAG_KEY, "1")
          sessionStorage.removeItem(SESSION_TOKEN_KEY)
        } else {
          sessionStorage.setItem(SESSION_TOKEN_KEY, token)
          localStorage.removeItem(SESSION_TOKEN_KEY)
          localStorage.removeItem(REMEMBER_FLAG_KEY)
        }
      }
    },

    /**
     * Clear authentication state across both storages.
     */
    clearAuth() {
      this._authenticatedUser = null
      this._sessionToken = null
      this._remember = false

      if (import.meta.client) {
        localStorage.removeItem(SESSION_TOKEN_KEY)
        localStorage.removeItem(REMEMBER_FLAG_KEY)
        sessionStorage.removeItem(SESSION_TOKEN_KEY)
      }
    },

    /**
     * Login a user with email and password.
     * @param {string} email
     * @param {string} password
     * @param {boolean} [remember=false] When true the token mirror is
     *   persisted in localStorage so the session survives browser
     *   restarts; when false it lives in sessionStorage.
     * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
     */
    async login(email, password, remember = false) {
      try {
        const { onLogin } = useApollo()
        const { mutate } = useMutation(LOGIN_MUTATION)

        const { data } = await mutate({ email, password })
        const result = data?.authenticateUserWithPassword

        // Check for failure response
        if (result?.__typename === "UserAuthenticationWithPasswordFailure") {
          this.clearAuth()

          return {
            success: false,
            error: result.message || "Invalid credentials",
          }
        }

        const userData = result?.item
        const sessionToken = result?.sessionToken

        // This app is for lead agency and company members ONLY. SysAdmin is a
        // technical account that manages the platform from the Keystone admin
        // panel — it never signs in here. The Keystone session the mutation
        // just created is ended so nothing lingers.
        if (userData?.role === ROLES.SYSTEM_ADMIN) {
          try {
            const { resolveClient } = useApolloClient()

            await resolveClient().mutate({ mutation: LOGOUT_MUTATION })
          } catch { /* the cookie is dropped client-side regardless */ }
          this.clearAuth()

          return {
            success: false,
            error: "This account does not have access to this part of the system.",
          }
        }

        if (userData && sessionToken) {
          this.setAuth(userData, sessionToken, remember)
          await onLogin(sessionToken)

          return { success: true, user: userData }
        }

        this.clearAuth()

        return { success: false, error: "Invalid login response" }
      } catch (err) {
        console.error("[AuthStore] Login error:", err)
        this.clearAuth()

        return {
          success: false,
          error: err.message || "Authentication failed",
        }
      }
    },

    /**
     * Fetch the authenticated user from the server
     * Used to restore session on app load
     * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
     */
    async fetchAuthenticatedUser() {
      try {
        const { onLogin, getToken } = useApollo()
        const { resolveClient } = useApolloClient()
        const client = resolveClient()

        const { data } = await client.query({
          query: AUTHENTICATED_USER_QUERY,
          fetchPolicy: "network-only",
        })

        const userData = data?.authenticatedItem

        if (userData) {
          const token = (await getToken()) || this._sessionToken

          // Restore path: keep whatever remember-mode init() detected
          // so we don't accidentally downgrade a "Remember me" session
          // to a per-tab one on a page refresh.
          this.setAuth(userData, token, this._remember)

          if (token) {
            await onLogin(token)
          }

          return { success: true, user: userData }
        }

        this.clearAuth()

        return { success: false, error: "No authenticated user" }
      } catch (err) {
        console.error("[AuthStore] Fetch authenticated user error:", err)
        this.clearAuth()

        return {
          success: false,
          error: err.message || "Failed to fetch authenticated user",
        }
      }
    },

    /**
     * Logout the current user and clear all authentication state.
     * Best-effort: any single step can fail (server unreachable, cache
     * already empty, etc.) without preventing the rest from running.
     * Navigation is up to the caller — this only owns the cleanup.
     */
    async logout() {
      this._isLoggingOut = true

      const { onLogout } = useApollo()
      const { resolveClient } = useApolloClient()

      // Execute logout mutation
      try {
        const client = resolveClient()

        await client.mutate({ mutation: LOGOUT_MUTATION })
      } catch (err) {
        console.error("[AuthStore] Logout mutation error:", err)
      }

      // Always clear auth state even if mutation fails
      this.clearAuth()

      // Notify Apollo of logout
      try {
        await onLogout()
      } catch (err) {
        console.error("[AuthStore] Apollo onLogout error:", err)
      }

      // Reset other stores
      this._resetRelatedStores()

      // Clear Apollo cache
      try {
        const client = resolveClient()

        await client.clearStore()
      } catch (err) {
        console.error("[AuthStore] Apollo clearStore error:", err)
      }

      this._isLoggingOut = false
    },

    /**
     * Reset related stores on logout
     * @private
     */
    _resetRelatedStores() {
      try {
        const casesStore = useCasesStore()
        const globalStore = useGlobalStore()

        casesStore.$reset()
        globalStore.$reset()

        // Stop every long-running composable: case-nav polling,
        // latest-updates polling, and the notification watcher's
        // seen-set. Otherwise the next login would either keep
        // hammering the server with the previous session or fire a
        // notification storm on first hydration.
        resetCaseNavState()
        resetLatestUpdatesState()
        resetFollowUpNotificationsState()

        // The V2 inboxes hold per-user work (whose calls, whose unseen notes),
        // so on a shared browser the next person must not inherit them. The
        // flags are re-read too — they're a server setting that may have moved.
        resetMessagesInbox()
        resetNotesInbox()
        resetUnifiedInbox()
        resetInboxFlags()

        // The mirrored-teammate inbox poll + its singleton state (the persisted
        // choice is per-user, but stop the poll so it doesn't outlive logout).
        resetInboxImpersonationState()

        // NOTE: we intentionally do NOT wipe persisted filters on logout.
        // They're keyed PER USER (`adminFilters:<userId>:<pageKey>`), so a
        // different user can't see them, and the SAME user gets their exact
        // filters back when they log in again. (See usePersistedFilters.)
      } catch (err) {
        console.error("[AuthStore] Store reset error:", err)
      }
    },

    /**
     * Check if the authenticated user has a specific permission
     * @param {string} permission - Permission constant from PERMISSIONS
     * @returns {boolean}
     */
    hasPermission(permission) {
      if (!this.isAuthenticated) {
        return false
      }

      switch (permission) {
      case PERMISSIONS.CASES_CAN_PLANNING_CASES:
        // Planning cases (the planning + assign bars) is a lead-agency-member
        // capability ONLY. SysAdmin is an administrative user with no user tag,
        // so they never plan — they don't see the planning UI. (Roles will be
        // restructured later; this is the first slice of that.)
        return this.isLeadAgencyUser
      case PERMISSIONS.FOLLOW_UPS_CHANGE_FOLLOWUP_INTERNAL:
      case PERMISSIONS.CASES_CHANGE_CASE_STAGE:
      case PERMISSIONS.CASES_CAN_ASSIGN_TAGS_TO_CASES:
        return this.isSysAdmin || this.isLeadAgencyUser
      case PERMISSIONS.FOLLOW_UPS_CAN_ASSIGN_CREATORS_TO_FOLLOW_UPS:
      case PERMISSIONS.CASES_TRANSFER_CASE:
        return this.isSysAdmin || this.isLeadAgencyUser
      case PERMISSIONS.CASES_IMPORT_PLANNING:
        // Any Lead Agency member (no SysAdmin / Company): VIEW a team member's
        // planning read-only ("View As" — right-click the planning bar). It's
        // read-only, so every lead-agency member gets it.
        return this.isLeadAgencyUser
      case PERMISSIONS.CASES_CHANGE_LEAD_SOURCE:
      case PERMISSIONS.FOLLOW_UPS_MARK_CASE_READ:
        // SysAdmin and Lead Agency Owner only. CASES_CHANGE_LEAD_SOURCE:
        // reassign a case's ABA source.
        // FOLLOW_UPS_MARK_CASE_READ: bulk "mark read" on the dashboard cards.
        return (
          this.isSysAdmin
          || this.authenticatedUser?.role === ROLES.LEAD_AGENCY_OWNER
        )
      case PERMISSIONS.CASES_EDIT_CASE:
        // Company users can edit cases too. The lead-agency field stays
        // locked for them (it's gated on CASES_CHANGE_LEAD_SOURCE above) and
        // the company field is always disabled in the modal.
        return this.isSysAdmin || this.isLeadAgencyUser || this.isCompanyUser
      case PERMISSIONS.CASES_VIEW_CASE_DETAILS:
      case PERMISSIONS.CASES_CAN_FILTER:
        // Read-only case capabilities for every case user (all roles today,
        // company members included): open the details modal (read-only unless
        // they also have CASES_EDIT_CASE) and filter the case list by ANY
        // filter (stage tag, insurance, missing docs, unread…). Distinct
        // from ASSIGNING tags / changing the stage, which are stricter. Future
        // roles can be excluded here.
        return this.isSysAdmin || this.isLeadAgencyUser || this.isCompanyUser
      case PERMISSIONS.STATS_VIEW:
      case PERMISSIONS.USERS_MANAGE:
      case PERMISSIONS.FOLLOW_UPS_REPORT_VIEW:
        // Platform administration inside the app belongs to the Lead Agency
        // OWNER (SysAdmin keeps it at API level, but no longer signs in here).
        return this.isSysAdmin
          || this.authenticatedUser?.role === ROLES.LEAD_AGENCY_OWNER
      default:
        return false
      }
    },

    /**
     * Check if user has one of the specified roles
     * @param {string[]} roles - Array of role constants from ROLES
     * @returns {boolean}
     */
    hasRole(roles) {
      if (!this.isAuthenticated) {
        return false
      }

      const userRole = this.authenticatedUser?.role

      return roles.includes(userRole)
    },
  },
})
